import { useEffect, useMemo, useRef, useState } from "react";
import { RiFilter3Line } from "react-icons/ri";
import Button from "../../Utility/Button/Button";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import Typography from "../../Utility/Typography";

function displayName(user) {
  const first = user?.firstName ?? "";
  const last = user?.lastName ?? "";
  const joined = [first, last].filter(Boolean).join(" ").trim();
  if (joined) return joined;
  const single = String(user?.name ?? user?.fullName ?? "").trim();
  if (single) return single;
  return String(user?.email ?? "").trim() || "User";
}

/**
 * Section order in the list (fixed): Admin → Manager → Developer → QA → Design → Other.
 * Match managers before developers; QA before developer regex so "test engineer" stays in QA.
 */
/** Map API role enums (e.g. `DEVELOPER`) before fuzzy regex on free-text titles. */
function resolveRoleEnumBand(roleNameRaw) {
  const key = String(roleNameRaw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (!key) return null;
  const map = {
    SUPER_ADMIN: { order: 0, title: "Admin" },
    ADMINISTRATOR: { order: 0, title: "Admin" },
    ADMIN: { order: 0, title: "Admin" },
    OWNER: { order: 0, title: "Admin" },
    MANAGER: { order: 1, title: "Manager" },
    PROJECT_MANAGER: { order: 1, title: "Manager" },
    PRODUCT_MANAGER: { order: 1, title: "Manager" },
    PROGRAM_MANAGER: { order: 1, title: "Manager" },
    ENGINEERING_MANAGER: { order: 1, title: "Manager" },
    DELIVERY_MANAGER: { order: 1, title: "Manager" },
    ACCOUNT_MANAGER: { order: 1, title: "Manager" },
    PRODUCT_OWNER: { order: 1, title: "Manager" },
    SCRUM_MASTER: { order: 1, title: "Manager" },
    DEVELOPER: { order: 2, title: "Developer" },
    SOFTWARE_ENGINEER: { order: 2, title: "Developer" },
    ENGINEER: { order: 2, title: "Developer" },
    PROGRAMMER: { order: 2, title: "Developer" },
    QA: { order: 3, title: "QA" },
    QC: { order: 3, title: "QA" },
    SDET: { order: 3, title: "QA" },
    TESTER: { order: 3, title: "QA" },
    QE: { order: 3, title: "QA" },
    QA_ENGINEER: { order: 3, title: "QA" },
    DESIGNER: { order: 4, title: "Design" },
    UX_DESIGNER: { order: 4, title: "Design" },
    UI_DESIGNER: { order: 4, title: "Design" },
    UX: { order: 4, title: "Design" },
    UI: { order: 4, title: "Design" },
  };
  return map[key] ?? null;
}

function getRoleBand(user) {
  const raw = user?.role;
  const roleName =
    typeof raw === "object" && raw != null ? String(raw.name ?? "") : String(raw ?? "");
  const enumBand = resolveRoleEnumBand(roleName);
  if (enumBand) return enumBand;

  const n = roleName.toLowerCase().replace(/_/g, " ");

  if (/\b(super\s*admin|administrator|admin|owner)\b/.test(n)) {
    return { order: 0, title: "Admin" };
  }

  if (
    /\b(manager|management)\b/.test(n) ||
    /project\s*manager|product\s*manager|program\s*manager|engineering\s*manager|delivery\s*manager|account\s*manager/.test(
      n
    ) ||
    /\bpm\b/.test(n) ||
    /scrum\s*master|product\s*owner/.test(n)
  ) {
    return { order: 1, title: "Manager" };
  }

  if (
    /\b(qa|qc|sdet)\b/.test(n) ||
    /quality(\s+assurance)?|\btester\b|test\s*(engineer|automation)|\bqe\b/.test(n)
  ) {
    return { order: 3, title: "QA" };
  }

  if (
    /developer|\bdev\b|software\s*engineer|\bengineer\b|programmer|full[\s-]?stack|frontend|backend|\bcoder\b|tech\s*lead|architect/.test(
      n
    )
  ) {
    return { order: 2, title: "Developer" };
  }

  if (/designer|design|\bux\b|\bui\b|ui\/ux/.test(n)) {
    return { order: 4, title: "Design" };
  }

  if (roleName.trim()) {
    return { order: 50, title: roleName.trim() };
  }
  return { order: 99, title: "Other" };
}

function buildGroupedUsers(users) {
  const valid = (users ?? []).filter((u) => {
    const id = u?.id ?? u?.userId;
    return id != null && String(id).trim() !== "";
  });

  const rows = valid.map((user) => ({ user, ...getRoleBand(user) }));
  rows.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return displayName(a.user).localeCompare(displayName(b.user), undefined, {
      sensitivity: "base",
    });
  });

  const groups = [];
  let currentTitle = null;
  let bucket = [];

  for (const row of rows) {
    if (row.title !== currentTitle) {
      if (currentTitle != null && bucket.length) {
        groups.push({ title: currentTitle, users: bucket });
      }
      currentTitle = row.title;
      bucket = [row.user];
    } else {
      bucket.push(row.user);
    }
  }
  if (currentTitle != null && bucket.length) {
    groups.push({ title: currentTitle, users: bucket });
  }
  return groups;
}

/**
 * Jira-style quick filter: multi-select users grouped by role (no avatars).
 */
export default function GlobalKanbanQuickFilter({
  users = [],
  selectedIds = [],
  onSelectionChange,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const grouped = useMemo(() => buildGroupedUsers(users), [users]);
  const normalizedSelectedIds = useMemo(
    () =>
      (Array.isArray(selectedIds) ? selectedIds : [])
        .map((x) => String(x ?? "").trim())
        .filter(Boolean),
    [selectedIds]
  );
  const count = normalizedSelectedIds.length;

  const toggleId = (rawId) => {
    const id = String(rawId);
    onSelectionChange((prev) => {
      const p = Array.isArray(prev) ? prev : [];
      return p.includes(id) ? p.filter((x) => x !== id) : [...p, id];
    });
  };

  const clearAll = () => onSelectionChange([]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <Button
        type="button"
        variant="neutral"
        size="vsm"
        onClick={() => setOpen((o) => !o)}
        className="!inline-flex !items-center !gap-2 !rounded-md !border !border-border-primary !bg-surface-primary !px-3 !py-2 !text-sm !font-medium !text-text-primary !shadow-sm hover:!bg-[var(--bg-secondary)]"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <RiFilter3Line className="text-lg text-text-secondary shrink-0" aria-hidden />
        <span>Quick filters</span>
        {count > 0 ? (
          <span className="rounded-full bg-[var(--theme-primary)] px-2 py-0.5 text-xs font-semibold text-white">
            {count}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          className="absolute right-0 z-[1200] mt-2 flex max-h-[min(24rem,calc(100dvh-12rem))] w-[min(100vw-2rem,20rem)] flex-col overflow-hidden rounded-lg border border-border-primary bg-surface-primary shadow-lg"
          role="dialog"
          aria-label="Quick filters"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border-primary px-3 py-2">
            <Typography variant="text2" className="font-semibold text-text-secondary">
              Filter by people
            </Typography>
            <Button
              type="button"
              variant="menu"
              size="vsm"
              disabled={count === 0}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              className="!shrink-0 !text-xs !font-medium !text-[var(--theme-primary)] disabled:!opacity-40"
            >
              Clear all
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
            {grouped.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-text-secondary">
                No users loaded.
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.title} className="mb-2 last:mb-0">
                  <div className="bg-surface-primary px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
                    {group.title}
                  </div>
                  <ul className="space-y-0.5">
                    {group.users.map((u) => {
                      const id = String(u.id ?? u.userId);
                      const checked = normalizedSelectedIds.includes(id);
                      return (
                        <li key={id}>
                          <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-[var(--bg-secondary)]">
                            <Checkbox
                              noWrapper
                              checked={checked}
                              onChange={() => toggleId(id)}
                              aria-label={displayName(u)}
                            />
                            <Typography
                              variant="span2"
                              className="flex-1 truncate text-text-primary"
                            >
                              {displayName(u)}
                            </Typography>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
